/**
 * Every href the ledger-link surfaces build: the journal panel's source line
 * and the statement rows. One module, so a destination is spelled once and the
 * two surfaces cannot disagree about where a record lives.
 */

import type { LedgerKind } from '@packages/types';

/** UI-facing ledger identity, spoken by the URL param and the switch labels.
 *  `ledgerKindToSwitchValue` below maps from the backend `LedgerKind`. */
export type LedgerSwitchValue = 'business' | 'trust';

export function ledgerKindToSwitchValue(kind: LedgerKind): LedgerSwitchValue {
  return kind === 'TRUST' ? 'trust' : 'business';
}

/**
 * Append the ledger context to a link target so navigation carries the current
 * ledger (business is the default and adds nothing). Uses the right separator,
 * so it composes whether or not `path` already has a query string - the single
 * place the `ledger=trust` param rule lives.
 */
export function appendLedgerParam(path: string, value: LedgerSwitchValue): string {
  if (value !== 'trust') return path;
  return `${path}${path.includes('?') ? '&' : '?'}ledger=trust`;
}

export function invoiceHref(invoiceId: string): string {
  return `/invoices/${invoiceId}`;
}

/** An expense is a supplier invoice everywhere except in front of the user. */
export function expenseHref(supplierInvoiceId: string): string {
  return `/expenses/${supplierInvoiceId}`;
}

/**
 * A fixed asset has no page of its own: the register on the Accounting tab
 * opens the asset named by `?asset=`, showing its depreciation schedule. Asset
 * journals only ever post to the business ledger, which is the page's default,
 * so no `ledger` param is needed.
 */
export function assetHref(assetId: string): string {
  return `/accounting?tab=assets&asset=${encodeURIComponent(assetId)}`;
}

/** Deep link to a journal on the given ledger's Journals tab. */
export function journalHref(ledgerEntrySetId: string, ledger: LedgerSwitchValue): string {
  return appendLedgerParam(`/accounting?tab=journals&journal=${encodeURIComponent(ledgerEntrySetId)}`, ledger);
}

/**
 * A bank line is a row on its bank account's page. `search` is the page's own
 * URL-synced filter, so the box arrives populated and the reader can see why
 * the list is short and clear it in one click. The backend's search matches a
 * UUID term against the line id for exactly this.
 */
export function bankTransactionHref(bankAccountId: string, bankStatementLineId: string): string {
  return `/bank-accounts/${bankAccountId}?search=${bankStatementLineId}`;
}
